import React, { useRef, useState, useEffect, useCallback } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    IconButton,
    TextField,
    Stack,
    CircularProgress,
    Divider,
} from "@mui/material";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ReplayIcon from "@mui/icons-material/Replay";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderOpenIcon from "@mui/icons-material/FolderOpen"; // Import new folder icon
import { useSelector } from "react-redux";
import ImageDetails from "./ImageDetails";
import toast from "react-hot-toast";

export const appServiceName = process.env.REACT_APP_BACKEND_URL;

const CameraCard = ({ id, onClose, user_id }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const fileInputRef = useRef(null); // Ref for device native file selection

    const { company_id } = useSelector((state) => state.GlobalVariables);
    const [facingMode, setFacingMode] = useState("user");
    const [image, setImage] = useState(null);
    const [imageBlob, setImageBlob] = useState(null);
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [changes,setchanges] = useState(false);

    // STOP CAMERA FUNCTION
    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setIsCameraActive(false);
    }, []);

    // START CAMERA FUNCTION
    const startCamera = useCallback(async (mode = facingMode) => {
        try {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
            }

            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: mode },
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                },
                audio: false,
            });

            streamRef.current = mediaStream;
            setIsCameraActive(true);

            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                }
            }, 50);

        } catch (error) {
            console.error("Camera access error:", error);
            // Block crash execution silently so user can still manually select local files
            setIsCameraActive(false);
        }
    }, [facingMode]);

    // INITIAL LOAD
    useEffect(() => {
        startCamera();
        return () => {
            stopCamera();
        };
    }, [startCamera, stopCamera]);

    const handleClose = () => {
        stopCamera();
        if (onClose) onClose();
    };

    const switchCamera = () => {
        const newMode = facingMode === "user" ? "environment" : "user";
        setFacingMode(newMode);
        startCamera(newMode);
    };

    // CAPTURE PHOTO FROM LIVE FEED
    const capturePhoto = async () => {
        try {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            if (!video || !canvas) return;

            const context = canvas.getContext("2d");
            canvas.width = video.videoWidth || 640;
            canvas.height = video.videoHeight || 480;

            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            const imageData = canvas.toDataURL("image/png");
            setImage(imageData);

            const blob = await new Promise((resolve) =>
                canvas.toBlob(resolve, "image/jpeg", 0.9)
            );
            setImageBlob(blob);
            stopCamera();
        } catch (error) {
            console.error("Capture runtime error:", error);
        }
    };

    // HANDLE LOCAL DEVICE FILE UPLOAD
    const handleLocalFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Strict File Validation
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(file.type)) {
            toast.error("Only .jpg, .jpeg, or .png images are supported!");
            return;
        }

        setImageBlob(file); // Store raw blob for API payload

        // FileReader implementation to generate direct base64 screen preview local path
        const reader = new FileReader();
        reader.onloadend = () => {
            toast.success("File selected successfully!");
            setImage(reader.result);
            stopCamera(); // Turn off camera grid feed if it was running background
        };
        reader.readAsDataURL(file);
    };

    // RETAKE OR RESET CHOSEN IMAGE
    const handleRetake = () => {
        setImage(null);
        setImageBlob(null);
        if (fileInputRef.current) fileInputRef.current.value = ""; // Empty input buffer reset
        startCamera(facingMode);
    };

    // SUBMIT FORM DATA MULTIPART STREAM
    const handleUpload = async () => {
        if (!imageBlob) {
            toast.error("Please capture an image or select a file first.");
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append("image", imageBlob, imageBlob.name || "capture.jpg");
            formData.append("employee_id", id);
            formData.append("description", description);
            formData.append("company_id", company_id);
            formData.append("user_id", user_id);

            const response = await fetch(`${appServiceName}/api/upload-image`, {
                method: "POST",
                body: formData,
            });
            if(response.status)
            {
                toast.success("Image uploaded successfully!");
                setchanges(prv => !prv);
                setImage(null);
                setImageBlob(null);
                setDescription("");
            }
            
        } catch (error) {
            console.error(error);
            toast.error("Upload failed. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 500, mx: "auto" }}>
            <Card
                sx={{
                    borderRadius: "24px",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                    background: "#1e293b",
                    color: "#fff",
                    overflow: "hidden"
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    {/* HEADER */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                        <Typography variant="h6" fontWeight="700" sx={{ letterSpacing: "-0.5px" }}>
                            Camera Verification & Upload
                        </Typography>
                        <IconButton onClick={handleClose} sx={{ color: "#94a3b8", "&:hover": { color: "#fff" } }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* VIEWPORT CONTROLLER BOX */}
                    <Box
                        sx={{
                            position: "relative",
                            borderRadius: "16px",
                            overflow: "hidden",
                            bgcolor: "#0f172a",
                            height: { xs: "280px", sm: "340px" },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid rgba(255,255,255,0.05)",
                            boxShadow: "inset 0 4px 20px rgba(0,0,0,0.4)"
                        }}
                    >
                        {!image ? (
                            <>
                                {isCameraActive ? (
                                    <>
                                        <video
                                            ref={videoRef}
                                            autoPlay
                                            playsInline
                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                        />
                                        <IconButton
                                            onClick={switchCamera}
                                            sx={{
                                                position: "absolute",
                                                top: 12,
                                                right: 12,
                                                bgcolor: "rgba(15, 23, 42, 0.75)",
                                                color: "#fff",
                                                backdropFilter: "blur(8px)",
                                                border: "1px solid rgba(255,255,255,0.1)"
                                            }}
                                        >
                                            <CameraswitchIcon fontSize="small" />
                                        </IconButton>
                                    </>
                                ) : (
                                    /* FALLBACK WHEN LIVE FEED PERMISSION BLOCKED OR INITIALIZING */
                                    <Box textAlign="center" p={3}>
                                        <Typography variant="body2" color="text.secondary" sx={{ color: "#64748b", mb: 2 }}>
                                            Camera stream is offline or not active.
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<ReplayIcon />}
                                            onClick={() => startCamera(facingMode)}
                                            sx={{ color: "#38bdf8", borderColor: "#38bdf8", textTransform: "none" }}
                                        >
                                            Retry Feed
                                        </Button>
                                    </Box>
                                )}
                            </>
                        ) : (
                            /* DISPLAY CHOSEN OR CAPTURED IMAGE */
                            <img src={image} alt="Captured Snapshot" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        )}
                    </Box>

                    {/* REMARKS INPUT DESCRIPTION */}
                    <TextField
                        fullWidth
                        label="Remarks / Description"
                        placeholder="e.g., Front Document, Site Picture"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        variant="filled"
                        sx={{
                            mt: 3,
                            bgcolor: "#0f172a",
                            borderRadius: "12px",
                            "& .MuiFilledInput-root": { color: "#fff", borderRadius: "12px", "&:before, &:after": { display: "none" } },
                            "& .MuiInputLabel-root": { color: "#64748b" },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#38bdf8" }
                        }}
                    />

                    {/* ACTION PANEL CONTROL SECTIONS */}
                    <Stack direction="column" spacing={1.5} mt={3}>
                        {!image ? (
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} width="100%">
                                {/* TRIGGER LIVE CAPTURE STREAM BUTTON */}
                                <Button
                                    fullWidth
                                    variant="contained"
                                    disabled={!isCameraActive}
                                    startIcon={<CameraAltIcon />}
                                    onClick={capturePhoto}
                                    sx={{
                                        borderRadius: "14px",
                                        py: 1.5,
                                        fontWeight: "600",
                                        textTransform: "none",
                                        background: "linear-gradient(135deg, #38bdf8 0%, #0369a1 100%)",
                                        boxShadow: "0 4px 14px rgba(56, 189, 248, 0.3)",
                                    }}
                                >
                                    Capture Photo
                                </Button>

                                {/* DEVICE LOCAL DOCUMENT SELECTOR ACTION BUTTON */}
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<FolderOpenIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{
                                        borderRadius: "14px",
                                        py: 1.5,
                                        fontWeight: "600",
                                        textTransform: "none",
                                        borderColor: "#64748b",
                                        color: "#cbd5e1",
                                        "&:hover": {
                                            borderColor: "#94a3b8",
                                            background: "rgba(255,255,255,0.04)"
                                        }
                                    }}
                                >
                                    Browse File
                                </Button>
                            </Stack>
                        ) : (
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} width="100%">
                                <Button
                                    fullWidth
                                    color="error"
                                    variant="outlined"
                                    startIcon={<ReplayIcon />}
                                    onClick={handleRetake}
                                    disabled={loading}
                                    sx={{ borderRadius: "14px", py: 1.5, fontWeight: "600", textTransform: "none" }}
                                >
                                    Reset / Retake
                                </Button>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                    onClick={handleUpload}
                                    disabled={loading}
                                    sx={{
                                        borderRadius: "14px",
                                        py: 1.5,
                                        fontWeight: "600",
                                        textTransform: "none",
                                        background: "linear-gradient(135deg, #4ade80 0%, #15803d 100%)",
                                    }}
                                >
                                    {loading ? "Uploading..." : "Upload"}
                                </Button>
                            </Stack>
                        )}
                    </Stack>

                    {/* HIDDEN WORKING STORES FOR FILES AND CANVAS BLOBS */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        hidden
                        accept="image/jpeg, image/jpg, image/png"
                        onChange={handleLocalFileChange}
                    />
                    <canvas ref={canvasRef} style={{ display: "none" }} />
                </CardContent>
            </Card>

            <ImageDetails employeeId={id} changes={changes} />
        </Box>
    );
};

export default CameraCard;